import React, {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import './ControlSortList.scss';
import { ControlSortListFilters } from '../../components/ControlSortListFilters/ControlSortListFilters';
import { HubSortListOrderEntry } from '../../hooks/useFetchHubSortList';
import { TabsComponent } from '../../ReusableComponents/Tabs/TabsComponent';
import { SortListTable } from './SortListTable';
import { isEqual, debounce } from 'lodash';
import { Region } from '../../types/orderTypes';
import { AllBundlesResponse } from 'fetch/fetchHubSortListEntriesAll';
import { ISortListBundle } from './SortListBundle';
import { useDeepCompareCallback } from 'use-deep-compare';
import TablePagination from '@material-ui/core/TablePagination';

const itemsPerPage = 5;

export type SortListBundleType = 'incompletedBundles' | 'completedBundles';

export function totalNumberOfPackages(bundle: HubSortListOrderEntry[]): number {
  return bundle.reduce((acc, cur) => {
    return acc + cur.order.packagesNumber;
  }, 0);
}

declare interface ControlSortListParameters {
  updateHubSortListEntries: (bundleIds: string[]) => Promise<void>;
  region?: Region;
  sortListBundles: AllBundlesResponse;
}

export interface SortListBundles {
  bundleOrders: HubSortListOrderEntry[];
}

const checkProps = (
  prevProps: Readonly<ControlSortListParameters>,
  nextProps: Readonly<ControlSortListParameters>
) =>
  isEqual(
    prevProps.sortListBundles.incompletedBundles,
    nextProps.sortListBundles.incompletedBundles
  ) &&
  isEqual(
    prevProps.sortListBundles.completedBundles,
    nextProps.sortListBundles.completedBundles
  );

const ControlSortList = memo(
  ({
    updateHubSortListEntries,
    sortListBundles,
  }: ControlSortListParameters): JSX.Element => {
    const [page, setPage] = useState(0);
    const filterSectionRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filterInputValue, setFilterInputValue] = useState<string>('');
    const [filteredBundles, setFilteredBundles] = useState<
      AllBundlesResponse | undefined
    >(undefined);
    const completedBundlesSenders = sortListBundles.completedBundles
      .flatMap(scanningItem => scanningItem.orders.filter(item => item.sender))
      .map(order => order.sender) as string[];
    const incompletedBundlesSenders = sortListBundles.incompletedBundles
      .flatMap(scanningItem => scanningItem.orders.filter(item => item.sender))
      .map(order => order.sender) as string[];
    const allSenders =
      completedBundlesSenders.length || incompletedBundlesSenders.length
        ? new Set([...completedBundlesSenders, ...incompletedBundlesSenders])
        : undefined;
    const [selectedSenders, setSelectedSenders] = useState<
      Set<string> | undefined
    >(allSenders);
    const [selectedTab, setSelectedTab] =
      useState<SortListBundleType>('incompletedBundles');

    const searcherListener = (event: KeyboardEvent) => {
      if (
        (event.key === 'f' || event.key === 'F') &&
        (event.ctrlKey || event.metaKey)
      ) {
        event.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
          filterSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const checkIfSenderMatch = useCallback(
      (
        selectedSenders: Set<string> | undefined,
        sendersFromOrders: string[]
      ): boolean =>
        sendersFromOrders
          .map(sender => selectedSenders?.has(sender))
          .includes(true),
      []
    );

    const filterBundles = useDeepCompareCallback(
      (
        bundles: ISortListBundle[],
        lowerCaseInput: string,
        selectedSendersParam?: Set<string>
      ) =>
        bundles.filter(bundle => {
          const senders = bundle.orders
            .filter(order => order.sender)
            .map(order => order.sender) as string[];
          const sendersFromOrders = senders.length
            ? Array.from(new Set(senders))
            : [];
          return (
            checkIfSenderMatch(
              selectedSendersParam || selectedSenders,
              sendersFromOrders
            ) &&
            (bundle.shelfId?.toLowerCase().includes(lowerCaseInput) ||
              bundle.driver?.toLowerCase().includes(lowerCaseInput) ||
              !!bundle.orders.find(hubOrder =>
                hubOrder.id.toString().includes(lowerCaseInput)
              ))
          );
        }),
      [checkIfSenderMatch, selectedSenders]
    );

    const debouncedFilterAction = useDeepCompareCallback(
      debounce((inputValue: string, selectedSendersParam?: Set<string>) => {
        if (
          inputValue ||
          selectedSendersParam ||
          selectedSenders?.size !== allSenders?.size
        ) {
          const lowerCaseInput = inputValue.toLowerCase();
          if (page !== 1) {
            setPage(1);
          }
          setFilteredBundles(() => {
            const incompletedBundles = filterBundles(
              sortListBundles.incompletedBundles,
              lowerCaseInput,
              selectedSendersParam
            );
            const completedBundles = filterBundles(
              sortListBundles.completedBundles,
              lowerCaseInput,
              selectedSendersParam
            );
            return { incompletedBundles, completedBundles };
          });
        } else {
          setFilteredBundles(undefined);
        }
      }, 500),
      [selectedTab, sortListBundles, filterBundles, selectedSenders, page]
    );

    const onChangeInput = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setFilterInputValue(event.currentTarget.value);
        debouncedFilterAction(event.currentTarget.value);
      },
      [debouncedFilterAction]
    );

    // eslint-disable-next-line @typescript-eslint/ban-types
    const tabChange = (event: ChangeEvent<{}>, value: string) => {
      setSelectedTab(value as SortListBundleType);
      setPage(1);
    };

    const handleCheckboxChange = useDeepCompareCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const senderName = event.currentTarget.name;
        const updatedSenders = new Set(selectedSenders);
        if (!selectedSenders?.has(senderName)) {
          updatedSenders.add(senderName);
          setSelectedSenders(updatedSenders);
        } else {
          updatedSenders.delete(senderName);
          setSelectedSenders(updatedSenders);
        }
        debouncedFilterAction(filterInputValue, updatedSenders);
      },
      [debouncedFilterAction, filterInputValue, selectedSenders]
    );
    const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
      if (filterSectionRef.current) {
        filterSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      setPage(value);
    };

    const pagination =
      filteredBundles?.[selectedTab]?.length ||
      sortListBundles[selectedTab].length ? (
        <TablePagination
          count={
            filteredBundles?.[selectedTab]?.length ||
            sortListBundles[selectedTab].length
          }
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={itemsPerPage}
          rowsPerPageOptions={[]}
        />
      ) : null;

    useEffect(() => {
      window.addEventListener('keydown', searcherListener);
      return function clearEvent() {
        window.removeEventListener('keydown', searcherListener);
      };
    }, []);

    return (
      <div className="sort-list__wrapper">
        <div
          className="sort-list__page__filter sort-list__filter"
          ref={filterSectionRef}
        >
          <ControlSortListFilters
            allSenders={allSenders}
            selectedSenders={selectedSenders}
            filterInputValue={filterInputValue}
            inputRef={inputRef}
            onCheckboxChange={handleCheckboxChange}
            onFilterInputChange={onChangeInput}
            onSetSelectedSenders={setSelectedSenders}
          />
        </div>
        <div>
          {pagination}
          <TabsComponent
            variant="fullWidth"
            scrollButtons="auto"
            onChange={tabChange}
            tabsChildren={[
              {
                tabClassName: 'sort-table-tab',
                contentClassName: 'sort-table-tab-contentv2',
                disabled: false,
                label: 'Incomplete Bundles',
                tabValue: 'incompletedBundles',
                tabPanelNode: (
                  <div className="bundles-wrapper">
                    {(
                      filteredBundles?.incompletedBundles ||
                      sortListBundles.incompletedBundles
                    )
                      .slice(
                        page * itemsPerPage,
                        page * itemsPerPage + itemsPerPage
                      )
                      .map((bundle, index) => (
                        <SortListTable
                          key={`${index}_${bundle.id}`}
                          updateHubSortListEntries={updateHubSortListEntries}
                          sortListBundles={bundle}
                        />
                      ))}
                  </div>
                ),
              },
              {
                tabClassName: 'sort-table-tab',
                contentClassName: 'sort-table-tab-contentv2',
                disabled: false,
                label: 'Completed Bundles',
                tabValue: 'completedBundles',
                tabPanelNode: (
                  <div className="bundles-wrapper">
                    {(
                      filteredBundles?.completedBundles ||
                      sortListBundles.completedBundles
                    )
                      .slice(
                        page * itemsPerPage,
                        page * itemsPerPage + itemsPerPage
                      )
                      .map((bundle, index) => (
                        <SortListTable
                          key={`${index}_${bundle.id}`}
                          updateHubSortListEntries={updateHubSortListEntries}
                          sortListBundles={bundle}
                        />
                      ))}
                  </div>
                ),
              },
            ]}
            value={selectedTab}
          />
          {pagination}
        </div>
      </div>
    );
  },
  checkProps
);

ControlSortList.displayName = 'ControlSortList';
ControlSortList.whyDidYouRender = true;
export default ControlSortList;
